import { isValidPhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';

const optionalString = () => yup.string().optional();

const optionalPhone = () =>
  optionalString().test(
    'valid-phone',
    'Enter a valid phone number.',
    (value) => !value || isValidPhoneNumber(value)
  );

const bankAccountSchema = yup.object({
  bank_name: optionalString(),
  routing_number: optionalString(),
  encryptedAccountNumber: optionalString(),
  account_type: optionalString(),
  web_site: optionalString(),
  is_ach_autopay: yup.boolean().optional(),
  notes: optionalString(),
});

const dependentSchema = yup.object({
  first_name: optionalString(),
  middle_name: optionalString(),
  last_name: optionalString(),
  encryptedSsn: optionalString(),
  birthdate: optionalString(),
  dependant_relationship_type: optionalString(),
});

export const createAccountValidationSchema = yup.object({
  name: yup.string().required('Please enter an account name.'),
  entity_type: yup.string().required('Please select an entity type.'),
  status: yup.string().required('Please select a status.'),
  stage: yup.string().when('status', {
    is: (status: string) =>
      !status || status.toLowerCase() !== 'client',
    then: (schema) => schema.required('Please select a stage.'),
    otherwise: (schema) => schema.optional(),
  }),
  is_restricted_account: yup.boolean().optional(),

  website: optionalString(),
  phone_number: optionalPhone(),
  fax_number: optionalPhone(),
  referral_source: optionalString(),
  rating: optionalString(),
  external_account_id: optionalString(),

  address_line_1: optionalString(),
  address_line_2: optionalString(),
  address_city: optionalString(),
  address_state: optionalString(),
  address_zip: optionalString(),
  address_country: optionalString(),

  encryptedCoEin: optionalString().test(
    'valid-ein',
    'Enter a valid EIN (9 digits).',
    (value) => {
      if (!value) return true;
      const digits = value.replace(/\D/g, '');
      return digits.length === 9;
    }
  ),
  co_industry: optionalString(),
  co_prior_accountant: optionalString(),
  co_number_of_employees: optionalString().test(
    'valid-employees',
    'Enter a valid number.',
    (value) => !value || /^\d+$/.test(value)
  ),
  co_fiscal_yearend: optionalString(),
  co_biggest_challenges: optionalString(),
  co_biz_decision_tools_used: optionalString(),
  co_expectations: optionalString(),

  co_tech_accounting: optionalString(),
  co_tech_expenses: optionalString(),
  co_tech_time_trackings: optionalString(),
  co_tech_payroll: optionalString(),
  co_tech_billpay: optionalString(),

  bankAccounts: yup.array().of(bankAccountSchema).optional(),

  taxpayer_first_name: optionalString(),
  taxpayer_middle_name: optionalString(),
  taxpayer_last_name: optionalString(),
  in_curr_filling_status: optionalString(),
  encryptedInSelfSsn: optionalString(),
  in_self_birthdate: optionalString(),
  in_self_driver_license: optionalString(),
  in_self_license_issue_date: optionalString(),
  in_self_license_expiry_date: optionalString(),
  in_self_license_state: optionalString(),

  in_sps_first_name: optionalString(),
  in_sps_middle_name: optionalString(),
  in_sps_last_name: optionalString(),
  encryptedInSpsSsn: optionalString(),
  in_sps_birthdate: optionalString(),
  in_sps_driver_license: optionalString(),
  in_sps_license_issue_date: optionalString(),
  in_sps_license_expiry_date: optionalString(),
  in_sps_license_state: optionalString(),
  in_sps_address_line_1: optionalString(),
  in_sps_address_line_2: optionalString(),
  in_sps_address_city: optionalString(),
  in_sps_address_state: optionalString(),
  in_sps_address_zip: optionalString(),
  in_sps_address_country: optionalString(),

  dependents: yup.array().of(dependentSchema).optional(),
});

export type CreateAccountBankAccountValues = yup.InferType<
  typeof bankAccountSchema
>;
export type CreateAccountDependentValues = yup.InferType<typeof dependentSchema>;
export type CreateAccountFormValues = yup.InferType<
  typeof createAccountValidationSchema
>;

export default createAccountValidationSchema;
